import axios from 'axios'

let ctx = {}

import {isTokenExpired} from '../assets/js/authentication'
import {getTimestampHash} from '../services/helper/url_timestamp_helper.js'
import {initResponseInterceptor} from './interceptors'
import {FST_REQUEST_AUTH_HEADER, TOKEN} from '../assets/js/constants'
import {useUserStore} from '@/store/user'
import {useAuthService} from '@/composables/auth_service'
import {isKeycloakAuth} from '@/composables/useAuthProvider'
import {useKeycloakService} from '@/composables/keycloak_service'
import {useAuthStore} from '@/store/auth'
import {buildKeycloakUser} from '@/composables/keycloak/useKeycloakToken'

// Shared in-flight refresh. Keycloak rotates the refresh token on every use, so two parallel requests
// must not each fire a refresh (the second would present an already-consumed token and be rejected).
// All concurrent callers await the same promise and receive the same freshly minted enriched token.
let keycloakRefreshPromise = null

const refreshKeycloakSession = async function(user) {
  if (!keycloakRefreshPromise) {
    keycloakRefreshPromise = (async () => {
      const userStore = useUserStore()
      const authStore = useAuthStore()
      const app = useNuxtApp()
      const userManager = app.$userManager

      let customerUuid = userStore.loadedCustomerUuid
      if (!customerUuid || customerUuid === 'null') {
        customerUuid = 'default'
      }

      const {refreshKeycloakToken} = useKeycloakService()
      const res = await refreshKeycloakToken(customerUuid, user.refresh_token)
      if (res && res.status === 200 && res.data && res.data.accessToken) {
        const newAccessToken = res.data.accessToken
        const newRefreshToken = res.data.refreshToken || user.refresh_token
        const newUser = buildKeycloakUser(newAccessToken, res.data.idToken || user.id_token, newRefreshToken, user.state)
        await userManager.storeUser(newUser)
        authStore.setUserObject(newUser)
        // Note: in an impersonation tab the userStore token is the impersonated identity; once it expires
        // we revert to the admin's own (durable) session here, which is the safe fallback.
        userStore.setAccessToken(newAccessToken)
        return newAccessToken
      }
      return null
    })().finally(() => { keycloakRefreshPromise = null })
  }
  return keycloakRefreshPromise
}

const create = function(...axiosConfig) {
  let runtimeConfig
  try {
    runtimeConfig = useRuntimeConfig()
  } catch {
    runtimeConfig = {public: process.env}
  }
  const localEnv = {SOURCE_KEY: runtimeConfig.public.SOURCE_KEY}
  let axiosApiInstance = axios.create(...axiosConfig)
  if (process.server) {
    initResponseInterceptor(axiosApiInstance, getContext)
    axiosApiInstance.interceptors.request.use(
      async config => {
        const result = await getTimestampHash(axios.getUri(config), localEnv)
        if (result && result.hash && result.timestamp) {
          if (config?.headers) {
            config.headers[FST_REQUEST_AUTH_HEADER.HASH] = result.hash
            config.headers[FST_REQUEST_AUTH_HEADER.TIMESTAMP] = result.timestamp
          }
        }
        return config
      },
      error => {
        Promise.reject(error)
      })

    return axiosApiInstance
  }

  initResponseInterceptor(axiosApiInstance, getContext)
  axiosApiInstance.interceptors.request.use(
    async config => {
      const userStore = useUserStore()
      if (userStore && userStore.accessToken) {
        try {
          const token = await getToken(`${config.baseURL}${config.url}`)
          if (token) {
            if (config?.headers) {
              // eslint-disable-next-line eqeqeq
              config.headers = config?.headers['Authorization'] != null ? config?.headers : {
                ...config.headers,
                'Authorization': `Bearer ${token}`
              }
            }
          } else {
            return null
          }
        } catch (e) {
          // ctx.error(e.message)
          // eslint-disable-next-line no-console
          console.error(e)
        }
      }
      // Add timestamp hash header
      const result = await getTimestampHash(axios.getUri(config), localEnv)
      if (result && result.hash && result.timestamp) {
        if (config?.headers) {
          config.headers[FST_REQUEST_AUTH_HEADER.HASH] = result.hash
          config.headers[FST_REQUEST_AUTH_HEADER.TIMESTAMP] = result.timestamp
        }
      }
      return config
    },
    error => {
      Promise.reject(error)
    })

  return axiosApiInstance
}

const setContext = function(ctxInstance) {
  ctx = ctxInstance
}

const getContext = function() {
  return ctx
}

const getToken = async function(requestorUrl) {
  const userStore = useUserStore()
  let token = userStore.accessToken

  const app = useNuxtApp()
  const userManager = app.$userManager

  const user = await userManager.getUser()
  // Keycloak: our enriched enext token and the oidc user expire together (expires_at = enriched exp),
  // so the B2C "user still valid, re-enrich" path below never triggers. Instead exchange the Keycloak
  // refresh token through our backend for a fresh enriched token, keeping the session alive.
  if (isKeycloakAuth()) {
    if (isTokenExpired(token, TOKEN.EXPIRE_BUFFER)) {
      if (user && user.refresh_token) {
        try {
          token = await refreshKeycloakSession(user)
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e)
          token = null
        }
      } else {
        token = null
      }
    }
  } else if (user && !user.expired && isTokenExpired(token, TOKEN.EXPIRE_BUFFER)) {
    let customerUuid = userStore.loadedCustomerUuid
    if (!customerUuid || customerUuid === 'null') {
      try {
        if (app.$sentry) {
          app.$sentry.captureException(new Error(`Customer Uuid was ${customerUuid ? '(string)null' : 'null'} for url ${requestorUrl}`))
        }
      } catch {
        // nothing to do really 
      }

      customerUuid = 'default'
    }
    const {getAccessToken} = useAuthService()
    let accessToken = user.access_token
    if (!accessToken.startsWith('Bearer ') && user.token_type === 'Bearer') {
      accessToken = `${user.token_type} ${accessToken}`
    }
    const res = await getAccessToken(customerUuid, accessToken)
    if (res && res.status === 200) {
      token = res.data.accessToken
      const userStore = useUserStore()
      if (userStore && token) {
        userStore.setAccessToken(res.data.accessToken)
      }
    }
    else {
      throw Error({message: 'No AccessToken received for url ' + requestorUrl})
    }
  } else if (isTokenExpired(token, TOKEN.EXPIRE_BUFFER)) {
    token = null
  }
  return token && !isTokenExpired(token, TOKEN.EXPIRE_BUFFER) ? token : null
}

export default {
  ...axios,
  create,
  setContext,
  getContext,
  getToken
}
